import React, { useState } from 'react';
import { products } from '../data/products';
import SafeIcon from '../common/SafeIcon';
import { FiBox, FiCheck, FiCpu, FiAlertCircle, FiLoader, FiTerminal } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const KitBuilder = ({ onNotify }) => {
  const [kit, setKit] = useState({ Power: null, Cable: null, Adapter: null });
  const [stage, setStage] = useState('selection'); // selection, validating, success
  const categories = ['Power', 'Cable', 'Adapter'];

  const selectItem = (category, product) => {
    setKit(prev => ({ ...prev, [category]: product }));
  };

  const total = Object.values(kit).reduce((sum, item) => sum + (item ? item.price : 0), 0);
  const isComplete = Object.values(kit).every(item => item !== null);

  const startDeployment = () => {
    setStage('validating');
    setTimeout(() => {
      setStage('success');
      onNotify('MANIFEST_VERIFIED // ORDER_INITIALIZED', 'success');
    }, 2500);
  };

  const resetKit = () => {
    setKit({ Power: null, Cable: null, Adapter: null });
    setStage('selection');
  };

  if (stage === 'success') {
    return (
      <section className="py-20 px-4 bg-graphite-900/50">
        <div className="max-w-3xl mx-auto dashboard-panel p-12 text-center border-green-500/30">
          <div className="w-20 h-20 bg-green-500/10 border border-green-500/50 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiCheck} className="text-4xl text-green-400" />
          </div>
          <h2 className="fids-text text-3xl font-black uppercase italic mb-4">Transmission_Successful</h2>
          <p className="fids-text text-sm text-gray-500 mb-10 max-w-md mx-auto uppercase leading-relaxed">
            Your custom travel configuration has been accepted. Logistics protocols have been initialized at your nearest node.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={resetKit} className="bg-electric text-graphite-900 fids-text px-8 py-3 font-bold hover:scale-105 transition-all uppercase text-xs">
              BUILD_NEW_KIT
            </button>
            <button className="border border-graphite-700 fids-text px-8 py-3 text-white hover:bg-white/5 transition-all uppercase text-xs">
              DOWNLOAD_MANIFEST
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-4 bg-graphite-900/50" id="builder">
      <div className="max-w-7xl mx-auto">
        <div className="dashboard-panel p-6 md:p-12 border-electric/10 overflow-hidden">
          {stage === 'validating' && (
            <div className="absolute inset-0 z-50 bg-graphite-900/90 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center">
              <SafeIcon icon={FiLoader} className="text-6xl text-electric animate-spin mb-6" />
              <div className="space-y-2">
                <h3 className="fids-text text-xl font-bold uppercase italic">Verifying_Manifest...</h3>
                <p className="fids-text text-[10px] text-gray-500 uppercase tracking-widest">Checking hardware compatibility & node availability</p>
              </div>
              <div className="mt-8 w-64 h-1 bg-graphite-800 rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 2 }} className="h-full bg-electric" />
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-8 items-start md:items-center mb-12">
            <div className="p-4 bg-electric/10 border border-electric/30 rounded-sm">
              <SafeIcon icon={FiBox} className="text-4xl text-electric" />
            </div>
            <div>
              <h2 className="fids-text text-3xl font-black uppercase italic tracking-tighter">Assembly_Terminal</h2>
              <p className="fids-text text-xs text-gray-500">CONSTRUCT YOUR PERSONAL TRAVEL MODULE PACKAGE</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-10">
              {categories.map(cat => (
                <div key={cat} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-px bg-electric/30"></span>
                    <h3 className="fids-text text-xs font-bold text-electric uppercase tracking-widest flex items-center gap-2">
                      <SafeIcon icon={FiTerminal} /> SELECT_{cat.toUpperCase()}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {products.filter(p => p.category === cat).map(product => (
                      <button
                        key={product.id}
                        onClick={() => selectItem(cat, product)}
                        className={`text-left p-4 border transition-all fids-text relative ${
                          kit[cat]?.id === product.id 
                            ? 'border-electric bg-electric/5' 
                            : 'border-graphite-700 hover:border-gray-500'
                        }`}
                        aria-pressed={kit[cat]?.id === product.id}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-sm font-bold text-white mb-1 uppercase">{product.name}</div>
                            <div className="text-[10px] text-gray-500 font-bold">${product.price.toFixed(2)}</div>
                          </div>
                          {kit[cat]?.id === product.id && <SafeIcon icon={FiCheck} className="text-electric" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-4">
              <div className="sticky top-24 bg-graphite-900 border border-graphite-700 p-8 space-y-8 h-fit">
                <h3 className="fids-text text-sm font-bold border-b border-graphite-700 pb-4 text-white">MANIFEST_READOUT</h3>
                
                <div className="space-y-6">
                  {categories.map(cat => (
                    <div key={cat} className="flex justify-between items-start fids-text text-[10px]">
                      <span className="text-gray-600 uppercase tracking-widest">{cat}_CORE</span>
                      <div className="text-right">
                        {kit[cat] ? (
                          <>
                            <div className="text-white font-bold uppercase">{kit[cat].name}</div>
                            <div className="text-electric font-bold">${kit[cat].price.toFixed(2)}</div>
                          </>
                        ) : (
                          <span className="text-red-400 flex items-center gap-1 uppercase">
                            <SafeIcon icon={FiAlertCircle} /> REQ_PENDING
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-8 border-t border-graphite-700">
                  <div className="flex justify-between items-center mb-8">
                    <span className="fids-text text-xs text-gray-600 uppercase">SYS_AGGR_PRICE</span>
                    <span className="fids-text text-3xl font-bold text-electric tracking-tighter">${total.toFixed(2)}</span>
                  </div>
                  
                  <button 
                    disabled={!isComplete}
                    onClick={startDeployment}
                    className={`w-full py-4 fids-text text-xs font-black transition-all shadow-lg uppercase tracking-widest ${
                      isComplete 
                      ? 'bg-electric text-graphite-900 hover:scale-[1.02] shadow-electric/20 active:scale-95' 
                      : 'bg-graphite-800 text-gray-600 cursor-not-allowed'
                    }`}
                  >
                    {isComplete ? 'INITIALIZE_DEPLOYMENT' : 'MANIFEST_INCOMPLETE'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KitBuilder;